import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './routes';
import { ToastHost } from './lib/toast';
import { ApiError } from './lib/api';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * QueryClient with enterprise-grade retry policy (CLAUDE.md §-1).
 *
 *  - 4xx (bad request, auth, not found) → never retry : these are client
 *    contract errors, hammering the server won't fix them.
 *  - 5xx + network errors + 408 timeouts → retry up to 2 times with
 *    exponential backoff (500 ms, 2 s) : transient blips recover, real
 *    outages still surface quickly.
 *  - Mutations retry once to absorb a flaky POST.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // 408 = our AbortController timeout. Treat as transient.
          if (error.status >= 400 && error.status < 500 && error.status !== 408) return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(2_000, 500 * 2 ** attempt),
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408) return false;
        return failureCount < 1;
      },
    },
  },
});

export default function App() {
  return (
    <ErrorBoundary label="Application" homeHref="/">
      <QueryClientProvider client={queryClient}>
        <ToastHost>
          <RouterProvider router={router} />
        </ToastHost>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
